/**
 * Navbar Component JavaScript
 * 处理导航栏的动态状态和交互
 */

// 防止重复声明 - 只在类不存在时创建
if (window.NavbarComponent) {
} else {

class NavbarComponent {
  constructor() {
    this.init();
  }

  init() {
    // 监听动态添加的导航栏
    this.observeNavbars();
    
    // 初始化现有导航栏
    this.initNavbars();
  }

  observeNavbars() {
    // 使用 MutationObserver 监听动态添加的导航栏
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // 检查新添加的元素是否是导航栏
            if (node.classList && node.classList.contains('component-navbar')) {
              this.initNavbar(node);
            }
            
            // 检查新添加的元素内部是否有导航栏
            const navbars = node.querySelectorAll && node.querySelectorAll('.component-navbar');
            if (navbars) {
              navbars.forEach(navbar => this.initNavbar(navbar));
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  initNavbars() {
    const navbars = document.querySelectorAll('.component-navbar');
    navbars.forEach(navbar => this.initNavbar(navbar));
  }

  initNavbar(navbar) {
    if (navbar.dataset.initialized) return;
    
    // 标记为已初始化
    navbar.dataset.initialized = 'true';

    // 绑定事件监听器
    this.bindEvents(navbar);
    this.setupResponsiveDesktopLayout(navbar);
  }

  bindEvents(navbar) {
    // 手机端菜单按钮点击事件
    const mobileMenuButton = navbar.querySelector('[data-menu-button]');
    if (mobileMenuButton) {
      mobileMenuButton.addEventListener('click', (e) => this.handleMobileMenuToggle(e, navbar));
    }

    // 点击外部关闭菜单
    document.addEventListener('click', (e) => this.handleOutsideClick(e, navbar));

    // 键盘事件
    document.addEventListener('keydown', (e) => this.handleKeyDown(e, navbar));

    // 滚动监听已移除，navbar默认有背景
  }

  setupResponsiveDesktopLayout(navbar) {
    const updateLayout = () => this.updateResponsiveDesktopLayout(navbar);
    navbar._navbarResponsiveUpdate = updateLayout;

    window.addEventListener('resize', updateLayout, { passive: true });
    requestAnimationFrame(updateLayout);
    setTimeout(updateLayout, 0);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(updateLayout).catch(() => {});
    }

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => updateLayout());
      resizeObserver.observe(navbar);

      const content = navbar.querySelector('.component-navbar__content');
      const logo = navbar.querySelector('.component-navbar__logo');
      const desktopMenu = navbar.querySelector('.component-navbar__desktop-menu');
      const rightActions = navbar.querySelector('.component-navbar__right-actions');

      if (content) resizeObserver.observe(content);
      if (logo) resizeObserver.observe(logo);
      if (desktopMenu) resizeObserver.observe(desktopMenu);
      if (rightActions) resizeObserver.observe(rightActions);

      navbar._navbarResizeObserver = resizeObserver;
    }
  }

  updateResponsiveDesktopLayout(navbar) {
    if (!navbar) {
      return;
    }

    const desktopBreakpoint = 1069;
    navbar.classList.remove('component-navbar--desktop-compact', 'component-navbar--force-tablet');

    if (window.innerWidth < desktopBreakpoint) {
      return;
    }

    const content = navbar.querySelector('.component-navbar__content');
    const logo = navbar.querySelector('.component-navbar__logo');
    const desktopMenu = navbar.querySelector('.component-navbar__desktop-menu');
    const rightActions = navbar.querySelector('.component-navbar__right-actions');

    if (!content || !logo || !desktopMenu || !rightActions) {
      return;
    }

    const contentWidth = this.getDesktopContentWidth(content);
    const logoWidth = Math.ceil(logo.getBoundingClientRect().width);
    const rightWidth = Math.ceil(rightActions.getBoundingClientRect().width);
    const desktopZoneGapTotal = 48 * 2;

    const defaultMenuWidth = this.getDesktopMenuRequiredWidth(desktopMenu, 48);
    if (contentWidth >= logoWidth + rightWidth + defaultMenuWidth + desktopZoneGapTotal) {
      this.closeMobileMenu(navbar);
      return;
    }

    const compactMenuWidth = this.getDesktopMenuRequiredWidth(desktopMenu, 32);
    if (contentWidth >= logoWidth + rightWidth + compactMenuWidth + desktopZoneGapTotal) {
      navbar.classList.add('component-navbar--desktop-compact');
      this.closeMobileMenu(navbar);
      return;
    }

    navbar.classList.add('component-navbar--force-tablet');
  }

  getDesktopMenuRequiredWidth(desktopMenu, gap) {
    if (!desktopMenu) {
      return 0;
    }

    const items = desktopMenu.querySelectorAll('.component-navbar__menu-item');
    if (!items.length) {
      return 0;
    }

    let totalWidth = 0;
    items.forEach((item) => {
      totalWidth += Math.ceil(item.getBoundingClientRect().width);
    });

    totalWidth += gap * Math.max(items.length - 1, 0);
    return totalWidth;
  }

  getDesktopContentWidth(content) {
    if (!content) {
      return 0;
    }

    const contentRect = content.getBoundingClientRect();
    const styles = window.getComputedStyle(content);
    const paddingLeft = parseFloat(styles.paddingLeft) || 0;
    const paddingRight = parseFloat(styles.paddingRight) || 0;

    return Math.ceil(contentRect.width - paddingLeft - paddingRight);
  }

  handleMobileMenuToggle(e, navbar) {
    e.preventDefault();
    e.stopPropagation();
    
    
    const isOpen = navbar.classList.contains('component-navbar--mobile-menu-open');
    if (isOpen) {
      this.closeMobileMenu(navbar);
    } else {
      this.openMobileMenu(navbar);
    }
  }

  handleOutsideClick(e, navbar) {
    const mobileMenu = navbar.querySelector('.component-navbar__mobile-menu');
    const mobileMenuButton = navbar.querySelector('[data-menu-button]');
    
    // 检查菜单是否打开
    const isMenuOpen = navbar.classList.contains('component-navbar--mobile-menu-open');
    
    // 如果菜单没有打开，不需要处理外部点击
    if (!isMenuOpen) {
      return;
    }
    
    
    // 如果点击的不是菜单按钮或菜单内容，则关闭菜单
    if (!navbar.contains(e.target) || 
        (e.target !== mobileMenuButton && !mobileMenuButton.contains(e.target) && 
         !mobileMenu.contains(e.target))) {
      this.closeMobileMenu(navbar);
    }
  }

  handleKeyDown(e, navbar) {
    // ESC 键关闭菜单
    if (e.key === 'Escape') {
      this.closeMobileMenu(navbar);
    }
  }

  // handleScroll 已移除，navbar默认有背景

  openMobileMenu(navbar) {
    navbar.classList.add('component-navbar--mobile-menu-open');
    
    // 检查菜单元素
    const mobileMenu = navbar.querySelector('.component-navbar__mobile-menu');
    if (mobileMenu) {
      // 菜单元素已找到，可以添加额外的初始化逻辑
    } else {
      console.error('Mobile menu element not found!');
    }
    
    // 触发自定义事件
    navbar.dispatchEvent(new CustomEvent('mobileMenuOpen', {
      detail: { navbar }
    }));
  }

  closeMobileMenu(navbar) {
    navbar.classList.remove('component-navbar--mobile-menu-open');
    
    // 检查菜单元素
    const mobileMenu = navbar.querySelector('.component-navbar__mobile-menu');
    if (mobileMenu) {
      // 菜单已关闭
    }
    
    // 触发自定义事件
    navbar.dispatchEvent(new CustomEvent('mobileMenuClose', {
      detail: { navbar }
    }));
  }

  // 公共方法：打开手机端菜单
  openMobileMenuPublic(navbar) {
    this.openMobileMenu(navbar);
  }

  // 公共方法：关闭手机端菜单
  closeMobileMenuPublic(navbar) {
    this.closeMobileMenu(navbar);
  }

  // 公共方法：切换手机端菜单
  toggleMobileMenu(navbar) {
    const isOpen = navbar.classList.contains('component-navbar--mobile-menu-open');
    
    if (isOpen) {
      this.closeMobileMenu(navbar);
    } else {
      this.openMobileMenu(navbar);
    }
  }

  // 公共方法：设置导航栏状态
  setNavbarState(navbar, state) {
    // 移除所有状态类
    navbar.classList.remove('component-navbar--enabled', 'component-navbar--disabled');
    
    // 添加新的状态类
    if (['enabled', 'disabled'].includes(state)) {
      navbar.classList.add(`component-navbar--${state}`);
    }
  }

  // 公共方法：设置导航栏颜色
  setNavbarColor(navbar, color) {
    // 移除所有颜色类
    navbar.classList.remove('component-navbar--black', 'component-navbar--white');
    
    // 添加新的颜色类
    if (['black', 'white'].includes(color)) {
      navbar.classList.add(`component-navbar--${color}`);
    }
  }
}

// 获取当前语言前缀
function getLanguagePrefix() {
  const pathname = window.location.pathname;
  // 匹配 /en-xx 或 /en-xx/ 格式的语言前缀
  const match = pathname.match(/^\/(en-[a-z]{2})(\/|$)/);
  return match ? match[1] : '';
}

// 构建带语言前缀的路径
function buildLocalizedPath(path) {
  const prefix = getLanguagePrefix();
  // 确保路径以 / 开头
  const cleanPath = path.startsWith('/') ? path : '/' + path;
  // 如果有语言前缀，添加到路径前
  return prefix ? '/' + prefix + cleanPath : cleanPath;
}

// Account点击处理函数
window.handleAccountClick = async function(event) {
  event.preventDefault();
  
  if (!window.authAPI) {
    window.location.href = window.withSitePrefix ? window.withSitePrefix('/pages/login') : buildLocalizedPath('/pages/login');
    return;
  }
  
  let loggedIn = false;
  if (typeof window.authAPI.isLoggedInStrict === 'function') {
    try {
      loggedIn = await window.authAPI.isLoggedInStrict();
    } catch (e) {
      loggedIn = false;
    }
  } else {
    try {
      loggedIn = !!window.authAPI.isLoggedIn();
      if (loggedIn && typeof window.authAPI.validateToken === 'function') {
        loggedIn = await window.authAPI.validateToken();
      }
    } catch (e) {
      loggedIn = false;
    }
  }
  
  if (!loggedIn) {
    window.location.href = window.withSitePrefix ? window.withSitePrefix('/pages/login') : buildLocalizedPath('/pages/login');
    return;
  }
  
  const isMobile = window.innerWidth <= 734;
  if (isMobile) {
    window.location.href = window.withSitePrefix ? window.withSitePrefix('/pages/mobile-menu') : buildLocalizedPath('/pages/mobile-menu');
  } else {
    window.location.href = window.withSitePrefix ? window.withSitePrefix('/pages/my-account') : buildLocalizedPath('/pages/my-account');
  }
};

// 导出类供外部使用
window.NavbarComponent = NavbarComponent;

// 页面加载完成后初始化（只初始化一次）
if (!window._navbarComponentInitialized) {
  document.addEventListener('DOMContentLoaded', () => {
    new NavbarComponent();
  });
  window._navbarComponentInitialized = true;
}

} // 结束防止重复声明的检查 
